import {clx} from '@hm/fundamental'
import {useMemo, useRef, useState, useCallback} from 'react'

import {DetailSummary} from './DetailSummary'
import {DrawingControls} from './DrawingControls'
import {DrawingInteractionControls} from './DrawingInteractionControls'
import {DrawingZoom} from './DrawingZoom'
import {
  clientToSvgPoint,
  createPolygonPath,
  getViewBox,
  rectsOverlap,
  toViewBoxString
} from './helpers'

import {
  StaticPartEditModesEnum,
  StaticPartInteractionModesEnum,
  StaticPartsColors
} from '@refra/modules/refra-static-parts/enums'
import {useDrawingPan, useDrawingZoom} from '@refra/modules/refra-static-parts/hooks'
import {useEditReplacement} from '@refra/modules/refra-static-parts/providers'
import {
  Point,
  Rect,
  StaticPartComponentDto,
  ViewBoxRect
} from '@refra/modules/refra-static-parts/types'

interface DetailDrawingProps {
  selectedComponent: StaticPartComponentDto
}

const DRAG_THRESHOLD = 0.25

export const DetailDrawing = ({selectedComponent}: DetailDrawingProps) => {
  const {editMode, interactionMode, methods, activeMaterialInstallationIndex} = useEditReplacement()
  const {setValue, getValues, watch} = methods

  const svgRef = useRef<SVGSVGElement | null>(null)
  const polygonRefs = useRef<Record<string, SVGPolygonElement | null>>({})

  const [dragStart, setDragStart] = useState<Point | null>(null)
  const [dragCurrent, setDragCurrent] = useState<Point | null>(null)

  const baseViewBoxRectRef = useRef<ViewBoxRect>(getViewBox(selectedComponent))
  const [viewBoxRect, setViewBoxRect] = useState<ViewBoxRect>(baseViewBoxRectRef.current)

  const isSelectingRef = useRef(false)
  const dragStartRef = useRef<Point | null>(null)

  const isDraggingRef = useRef(false)
  const shouldSuppressClickRef = useRef(false)
  const isPointerCapturedRef = useRef(false)
  const activePointerIdRef = useRef<number | null>(null)

  const {
    zoomPercentage,
    isAtDefaultView,
    canZoomIn,
    canZoomOut,
    handleZoomIn,
    handleZoomOut,
    handleZoomReset
  } = useDrawingZoom({
    svg: svgRef.current,
    baseViewBoxRect: baseViewBoxRectRef.current,
    viewBoxRect,
    setViewBoxRect
  })

  const {isPanning, handlePanStart, handlePanMove, handlePanEnd, handlePanCancel} = useDrawingPan({
    svg: svgRef.current,
    viewBoxRect,
    setViewBoxRect,
    activePointerIdRef,
    isPointerCapturedRef,
    shouldSuppressClickRef
  })

  const isMoveMode = interactionMode === StaticPartInteractionModesEnum.MOVE

  const selectionRect: Rect | null = useMemo(() => {
    if (!dragStart || !dragCurrent) return null
    const x = Math.min(dragStart.x, dragCurrent.x)
    const y = Math.min(dragStart.y, dragCurrent.y)
    const width = Math.abs(dragStart.x - dragCurrent.x)
    const height = Math.abs(dragStart.y - dragCurrent.y)
    return {x, y, width, height}
  }, [dragStart, dragCurrent])

  const setSelectedSectionIds = useCallback(
    (value: number[] | ((prev: number[]) => number[])) => {
      const materialInstallations = getValues('materialInstallations')
      const currentSectionIds = materialInstallations[activeMaterialInstallationIndex].sectionIds
      const newValue = typeof value === 'function' ? value(currentSectionIds) : value

      if (editMode === StaticPartEditModesEnum.ADD) {
        materialInstallations.forEach((mi, index) => {
          if (index === activeMaterialInstallationIndex) return

          const otherSectionIds = mi.sectionIds
          const hasOverlap = newValue.some((id) => otherSectionIds.includes(id))

          if (hasOverlap) {
            const nextOtherSectionIds = otherSectionIds.filter((id) => !newValue.includes(id))
            setValue(`materialInstallations.${index}.sectionIds`, nextOtherSectionIds, {
              shouldDirty: true,
              shouldTouch: true
            })
          }
        })
      }

      setValue(
        `materialInstallations.${activeMaterialInstallationIndex}.sectionIds`,
        [...new Set(newValue)],
        {
          shouldDirty: true,
          shouldTouch: true
        }
      )
    },
    [activeMaterialInstallationIndex, getValues, setValue, editMode]
  )

  const materialInstallations = watch('materialInstallations')

  const applySelectionFromRect = (rect: Rect) => {
    const newSelectedSectionIds: Set<number> = new Set()

    selectedComponent.sections.forEach((section) => {
      const el = polygonRefs.current[String(section.id)]
      if (!el) return

      const bbox = el.getBBox()
      const polyRect: Rect = {x: bbox.x, y: bbox.y, width: bbox.width, height: bbox.height}
      if (rectsOverlap(rect, polyRect)) newSelectedSectionIds.add(section.id)
    })

    setSelectedSectionIds((prev) => {
      if (editMode === StaticPartEditModesEnum.ADD)
        return [...new Set([...prev, ...newSelectedSectionIds])]
      if (editMode === StaticPartEditModesEnum.REMOVE)
        return prev.filter((id) => !newSelectedSectionIds.has(id))
      return prev
    })
  }

  const toggleSectionSelection = (sectionId: number) => {
    const materialInstallations = getValues('materialInstallations')
    const sectionIds = materialInstallations?.[activeMaterialInstallationIndex]?.sectionIds ?? []
    if (editMode === StaticPartEditModesEnum.ADD && !sectionIds.includes(sectionId)) {
      setSelectedSectionIds([...sectionIds, sectionId])
    }
    if (editMode === StaticPartEditModesEnum.REMOVE && sectionIds.includes(sectionId)) {
      setSelectedSectionIds(sectionIds.filter((id) => id !== sectionId))
    }
  }

  const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    if (isPanAllowed()) {
      e.preventDefault()
      handlePanStart(e)
      return
    }

    const svgPoint = clientToSvgPoint(e, svgRef)
    if (!svgPoint) return

    activePointerIdRef.current = e.pointerId
    isPointerCapturedRef.current = false
    isDraggingRef.current = false
    shouldSuppressClickRef.current = false

    isSelectingRef.current = true
    dragStartRef.current = svgPoint

    setDragStart(svgPoint)
    setDragCurrent(svgPoint)

    function isPanAllowed() {
      const isPanGesture = (e.button === 0 && e.shiftKey) || (isMoveMode && e.button === 0)
      if (!isPanGesture) return false
      if (isMoveMode) return true

      const targetElement = e.target as Element

      if (!targetElement) return false

      const isBackground = targetElement === svgRef.current
      const isAnnotation =
        targetElement.closest('#annotationShapes') !== null ||
        targetElement.closest('#annotationLabels') !== null

      const isSection = targetElement.closest('#sections') !== null

      return (isBackground || isAnnotation) && !isSection
    }
  }

  const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (isPanning) {
      handlePanMove(e)
      return
    }

    if (!isSelectingRef.current) return
    const svgPoint = clientToSvgPoint(e, svgRef)
    if (!svgPoint) return

    setDragCurrent(svgPoint)

    const start = dragStartRef.current
    if (start && !isDraggingRef.current) {
      const dragXDistance = svgPoint.x - start.x
      const dragYDistance = svgPoint.y - start.y
      const dragDistance = Math.hypot(dragXDistance, dragYDistance)

      if (dragDistance >= DRAG_THRESHOLD) {
        isDraggingRef.current = true
        shouldSuppressClickRef.current = true

        if (!isPointerCapturedRef.current) {
          e.currentTarget.setPointerCapture(e.pointerId)
          isPointerCapturedRef.current = true
        }
      }
    }
  }

  const onPointerUp = (e: React.PointerEvent<SVGSVGElement>) => {
    if (isPanning) {
      handlePanEnd(e)
      return
    }

    if (!isSelectingRef.current) return

    isSelectingRef.current = false
    dragStartRef.current = null

    setDragStart(null)
    setDragCurrent(null)

    if (
      isDraggingRef.current &&
      selectionRect &&
      selectionRect.width > 0 &&
      selectionRect.height > 0
    ) {
      applySelectionFromRect(selectionRect)
    }

    shouldSuppressClickRef.current = false
    isDraggingRef.current = false

    if (isPointerCapturedRef.current && activePointerIdRef.current !== null) {
      e.currentTarget.releasePointerCapture(activePointerIdRef.current)
    }
    isPointerCapturedRef.current = false
    activePointerIdRef.current = null
  }

  const onPointerLeave = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!isPanning && !isSelectingRef.current) return

    if (isPointerCapturedRef.current) return

    if (isPanning) {
      handlePanCancel(e)
    }

    isSelectingRef.current = false
    isDraggingRef.current = false
    dragStartRef.current = null

    setDragStart(null)
    setDragCurrent(null)

    if (isPointerCapturedRef.current && activePointerIdRef.current !== null) {
      e.currentTarget.releasePointerCapture(activePointerIdRef.current)
    }

    isPointerCapturedRef.current = false
    activePointerIdRef.current = null
    shouldSuppressClickRef.current = false
  }

  const getFillColor = (sectionId: number) => {
    const areaIndex = (materialInstallations || []).findIndex((mi) =>
      mi.sectionIds.includes(sectionId)
    )
    if (areaIndex === -1) return undefined

    return StaticPartsColors[areaIndex % StaticPartsColors.length]
  }

  return (
    <div className="relative w-full h-full flex-1 min-h-0">
      <DetailSummary />
      <div className="absolute right-4 top-4 z-10 flex items-center gap-4">
        <DrawingInteractionControls />
        <DrawingControls />
      </div>
      <DrawingZoom
        zoomPercentage={zoomPercentage}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onReset={handleZoomReset}
        canZoomIn={canZoomIn}
        canZoomOut={canZoomOut}
        isAtDefaultZoom={isAtDefaultView}
      />
      <div className="w-full h-[calc(100vh-360px)]">
        <svg
          ref={svgRef}
          viewBox={toViewBoxString(viewBoxRect)}
          style={{touchAction: 'none'}}
          className={clx(
            `block mx-auto h-full w-full bg-slate-200 ${
              isPanning
                ? 'cursor-grabbing'
                : dragStart
                  ? 'cursor-crosshair'
                  : interactionMode === StaticPartInteractionModesEnum.MOVE
                    ? 'cursor-grab'
                    : 'cursor-default'
            }`
          )}
          preserveAspectRatio="xMidYMid meet"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerLeave}
        >
          <g id="annotationShapes" className="select-none" pointerEvents="none">
            {selectedComponent.annotationShapes?.map((annotationShape, index) => (
              <polygon
                key={`${selectedComponent.id}-annotationShape-${index}`}
                id={`annotationShape-${index}`}
                points={createPolygonPath(annotationShape.vertices)}
                className={clx(
                  'stroke-black stroke-[0.03] fill-transparent',
                  !isMoveMode ? 'cursor-default' : 'cursor-pointer'
                )}
              />
            ))}
          </g>

          <g id="annotationLabels" className="select-none" pointerEvents="none">
            {selectedComponent.annotationLabels?.map((annotationLabel, index) => (
              <text
                key={`${selectedComponent.id}-annotationLabel-${index}`}
                x={annotationLabel.position.x}
                y={-annotationLabel.position.y}
                transform={`rotate(${annotationLabel.rotation}, ${annotationLabel.position.x}, ${-annotationLabel.position.y})`}
                fontSize={annotationLabel.height}
                textAnchor="middle"
                dominantBaseline="middle"
                pointerEvents="none"
              >
                {annotationLabel.text}
              </text>
            ))}
          </g>

          <g id="sections" pointerEvents={isMoveMode ? 'none' : undefined}>
            {selectedComponent.sections.map((section) => {
              const sectionId = section.id

              return (
                <polygon
                  key={`${selectedComponent.id}-polygon-${section.id}`}
                  id={`polygon-${section.id}`}
                  ref={(el) => {
                    polygonRefs.current[String(sectionId)] = el
                  }}
                  points={createPolygonPath(section.vertices)}
                  className={clx(
                    'fill-white stroke-black stroke-[0.03] cursor-cell active:cursor-crosshair',
                    !isMoveMode && 'transition-all duration-200 hover:fill-blue-200'
                  )}
                  style={{
                    fill: getFillColor(sectionId)
                  }}
                  onPointerUp={(e) => {
                    if (shouldSuppressClickRef.current) {
                      e.preventDefault()
                      e.stopPropagation()
                      return
                    }
                    toggleSectionSelection(sectionId)
                  }}
                  onPointerDown={(e) => {
                    e.preventDefault()
                  }}
                />
              )
            })}
          </g>

          {selectionRect && (
            <rect
              x={selectionRect.x}
              y={selectionRect.y}
              width={selectionRect.width}
              height={selectionRect.height}
              fill="rgba(59, 130, 246, 0.25)"
              stroke="rgba(59, 130, 246, 0.9)"
              strokeWidth={0.1}
              vectorEffect="non-scaling-stroke"
              pointerEvents="none"
            />
          )}
        </svg>
      </div>
    </div>
  )
}
